import { toast } from "react-toastify";
import { IndustryEngagement, IIndustryEngagementQueryParams } from "models/industry-engagement";
import { PaginatedResult } from "models/queryable";
import { IndustryEngagementStatus } from "models/industry-engagement";
import Employee from "models/employee";
import Opportunity from "models/opportunity";
import IndustryEngagementFields from "Components/organisms/forms/industry-engagement-form/types";

export const getIndustryEngagements = async (
    query?: IIndustryEngagementQueryParams
): Promise<PaginatedResult<IndustryEngagement> | null> => {
    try {
        let queryString = '';
        if (query) {
            const params = new URLSearchParams();

            Object.entries(query).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '') return;

                if (Array.isArray(value)) {
                    value.forEach(v => {
                        if (v) params.append(key, v);
                    });
                } else {
                    params.append(key, String(value));
                }
            });

            queryString = params.toString();
        }

        const url = `${process.env.REACT_APP_API_URL}/industry-engagement${queryString ? `?${queryString}` : ''}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data as PaginatedResult<IndustryEngagement>;

    } catch (err: any) {
        toast.error(err.message ?? "Error getting Industry Engagements");
        return null;
    }
}

export const getIndustryEngagementSearchFields = async (): Promise<{
    statuses: IndustryEngagementStatus[];
    opportunities: Opportunity[];
    employees: Employee[];
} | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/industry-engagement/search-fields`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data as {
            statuses: IndustryEngagementStatus[];
            opportunities: Opportunity[];
            employees: Employee[];
        };
    } catch (err: any) {
        toast.error(err.message ?? "Error getting industry engagement search fields");
        return null;
    }
}

export const submitCreateIndustryEngagementForm = async (
    vals: IndustryEngagementFields,
    handleResetForm: () => any
): Promise<IndustryEngagement | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/industry-engagement`;

        const keys = Object.keys(vals);
        keys.forEach(k => {
            if (vals[k] === '')
                vals[k] = undefined;
        });

        const res = await fetch(url, {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify(vals),
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Industry Engagement Created.");
        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error creating Industry Engagement");
        return null;
    }
}

export const submitEditIndustryEngagementForm = async (
    industryEngagementId: string,
    vals: IndustryEngagementFields,
    handleResetForm: () => any
): Promise<IndustryEngagement | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/industry-engagement/${industryEngagementId}`;

        const keys = Object.keys(vals);
        keys.forEach(k => {
            if (vals[k] === '')
                vals[k] = undefined;
        });

        const res = await fetch(url, {
            credentials: 'include',
            method: 'PATCH',
            body: JSON.stringify(vals),
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Industry Engagement updated.");
        handleResetForm();
        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error editing Industry Engagement");
        return null;
    }
}

export const getIndustryEngagement = async (
    industryEngagementId: string
): Promise<IndustryEngagement | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/industry-engagement/${industryEngagementId}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error getting Industry Engagement");
        return null;
    }
}

export const deleteIndustryEngagementRequest = async (
    industryEngagementId: string
): Promise<boolean> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/industry-engagement/${industryEngagementId}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'DELETE',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Industry Engagement deleted.");
        return true;
    } catch (err: any) {
        toast.error(err.message ?? "Error deleting Industry Engagement");
        return false;
    }
}